import { Capacitor } from "@capacitor/core";
import { Directory, Filesystem } from "@capacitor/filesystem";
import type { SupabaseClient } from "@supabase/supabase-js";
import { dbErrorMessage, logDbError } from "../supabase/db-error";
import { containerRootFromDirUri, rewriteToCurrentContainer } from "./audio-path";
import { buildResumableEndpoint } from "./resumable-endpoint";
import { defaultResumableUpload, type ResumableUploadFn } from "./resumable-upload";
import type { Capture } from "./schema";
import { mapCaptureToDbRow } from "./supabase-mapper";
import {
  AUDIO_INTROUVABLE_SUR_APPAREIL,
  AUDIO_NON_LU,
  CAPTURE_NON_ENREGISTREE,
  httpDetail,
  missingSessionFailure,
} from "./upload-error";

// Exporté : réutilisé par segment-upload.ts (dernier recours, spec §5.2.2) pour
// que le nom du bucket et le type MIME restent à un seul endroit.
export const BUCKET = "captures-audio";
export const AUDIO_MIME = "audio/mp4";

export type UploadCaptureResult =
  | { ok: true }
  // `detail` : motif technique, jamais affiché (classement de l'échec, signalement).
  | { ok: false; error: string; audioMissing?: boolean; detail?: string };

export function buildAudioStoragePath(userId: string, captureId: string): string {
  return `${userId}/${captureId}.m4a`;
}

// L'erreur de lecture vient-elle d'un fichier audio DÉFINITIVEMENT absent (≠ erreur
// transitoire) ? Sur iOS, un .m4a écrit dans `tmp/` peut être purgé par l'OS : le
// plugin Filesystem lève alors le code `OS-PLUG-FILE-0008` (fichier inexistant). Ce
// cas mène à un ABANDON IRRÉVERSIBLE de la capture (retirée du buffer) → on exige le
// signal FIABLE qu'est le code, et PAS un simple match de message : un texte large
// (« does not exist » dans une erreur transitoire, localisée, ou d'une autre couche)
// pourrait supprimer une capture dont l'audio existe encore = perte de données. Les
// erreurs de lecture transitoires iOS portent un autre code (ex. 0013) → gardées.
export function isMissingAudioFileError(err: unknown): boolean {
  const code = typeof err === "object" && err !== null ? (err as { code?: unknown }).code : null;
  return code === "OS-PLUG-FILE-0008";
}

export type ReadAudioFn = (uri: string) => Promise<Blob>;

function base64ToBlob(base64: string, mime: string): Blob {
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
  return new Blob([bytes], { type: mime });
}

async function readNativeFileBase64(path: string): Promise<string> {
  const r = await Filesystem.readFile({ path });
  return typeof r.data === "string" ? r.data : await r.data.text();
}

// I/O natif injectable : permet de tester la logique de lecture (réécriture de
// container + règles de conclusion « absent ») sans device ni mock de module.
export interface NativeAudioIo {
  readFileBase64(path: string): Promise<string>;
  getLibraryUri(): Promise<string>;
}

// Lecture native durcie (finding QA resync #1). L'UUID du container a pu CHANGER
// depuis l'enregistrement (réinstall / migration iOS) : on lit d'abord le chemin
// réécrit vers le container COURANT. Règle de sûreté — un 0008 mène à l'ABANDON
// IRRÉVERSIBLE de la capture, donc on ne conclut « absent » que si le chemin du
// container courant répond LUI-MÊME 0008 :
// - erreur transitoire sur le chemin courant → propagée telle quelle (réessai au
//   prochain trigger), JAMAIS de repli vers le chemin d'origine périmé qui ferait
//   surgir un 0008 trompeur (l'ancien comportement = vecteur de perte de données) ;
// - 0008 au courant → dernier recours : le chemin d'origine tel quel (si lui aussi
//   répond 0008, le fichier est vraiment absent des deux emplacements possibles) ;
// - dérivation du container impossible → un 0008 du chemin d'origine est NON
//   concluant : re-jeté SANS code (message conservé) pour rester transitoire.
export async function readNativeAudioBase64(uri: string, io: NativeAudioIo): Promise<string> {
  let candidate = uri;
  let currentContainerKnown = false;
  try {
    const root = containerRootFromDirUri(await io.getLibraryUri(), "Library");
    if (root) {
      candidate = rewriteToCurrentContainer(uri, root);
      currentContainerKnown = true;
    }
  } catch {
    // getUri indisponible : on lira le chemin d'origine, sans certitude sur le
    // container courant (voir règle ci-dessus).
  }
  try {
    return await io.readFileBase64(candidate);
  } catch (err) {
    if (!isMissingAudioFileError(err)) throw err;
    if (!currentContainerKnown) {
      throw new Error(err instanceof Error ? err.message : "Lecture audio échouée");
    }
    if (candidate === uri) throw err;
    return await io.readFileBase64(uri);
  }
}

const capacitorAudioIo: NativeAudioIo = {
  readFileBase64: readNativeFileBase64,
  getLibraryUri: async () =>
    (await Filesystem.getUri({ directory: Directory.Library, path: "" })).uri,
};

export async function defaultReadAudio(uri: string): Promise<Blob> {
  if (Capacitor.isNativePlatform()) {
    // WKWebView refuse fetch(file://) sur iOS → passer par Filesystem natif.
    return base64ToBlob(await readNativeAudioBase64(uri, capacitorAudioIo), AUDIO_MIME);
  }
  const response = await fetch(uri);
  if (!response.ok) {
    throw new Error(`Lecture audio échouée (HTTP ${response.status})`);
  }
  return response.blob();
}

interface UploadCaptureInput {
  client: SupabaseClient;
  capture: Capture;
  supabaseUrl: string;
  anonKey: string;
  readAudio?: ReadAudioFn;
  resumableUpload?: ResumableUploadFn;
  onProgress?: (pct: number) => void;
}

type ReadAudioOutcome =
  | { ok: true; blob: Blob }
  // `detail` : motif technique, jamais affiché (classement de l'échec, signalement).
  | { ok: false; error: string; audioMissing?: boolean; detail?: string };

// Lit le blob audio, en traduisant un échec en résultat (jamais un throw). Isolé
// d'`uploadCapture` pour garder cette dernière simple. Un fichier définitivement
// absent est marqué `audioMissing` → le filet de re-synchro abandonnera la capture
// au lieu de la ré-essayer en boucle.
async function readAudioForUpload(
  readAudio: ReadAudioFn,
  audioPath: string,
): Promise<ReadAudioOutcome> {
  try {
    return { ok: true, blob: await readAudio(audioPath) };
  } catch (err) {
    // DS §6.13 : le motif du module natif part au journal. Le cas « fichier
    // disparu » garde son drapeau `audioMissing` — c'est lui qui déclenche la
    // bannière dédiée de l'accueil (AbandonedNotice), qui a son propre texte.
    const detail = err instanceof Error ? err.message : String(err);
    logDbError("captures.upload.readAudio", detail);
    if (isMissingAudioFileError(err)) {
      return { ok: false, error: AUDIO_INTROUVABLE_SUR_APPAREIL, audioMissing: true, detail };
    }
    return { ok: false, error: AUDIO_NON_LU, detail };
  }
}

export async function uploadCapture(input: UploadCaptureInput): Promise<UploadCaptureResult> {
  const { client, capture, supabaseUrl, anonKey } = input;
  const readAudio = input.readAudio ?? defaultReadAudio;
  const resumableUpload = input.resumableUpload ?? defaultResumableUpload;

  let storagePath: string | null = null;

  if (capture.keepAudio) {
    if (!capture.audioPath) {
      // Incohérence interne (une capture qui garde son audio sans chemin) :
      // c'était un message de développeur affiché à l'écran.
      logDbError("captures.upload", "audioPath manquant alors que keepAudio=true");
      return { ok: false, error: AUDIO_INTROUVABLE_SUR_APPAREIL, audioMissing: true };
    }
    const read = await readAudioForUpload(readAudio, capture.audioPath);
    if (!read.ok) return read;
    const blob = read.blob;

    const {
      data: { session },
      error: sessionError,
    } = await client.auth.getSession();
    // Session nulle sur un rafraîchissement impossible (réseau, 502/503/504)
    // ⇒ réseau, pas session (C20, tour 4).
    if (!session) return missingSessionFailure("captures.upload.session", sessionError);

    storagePath = buildAudioStoragePath(capture.createdBy, capture.id);
    const uploadResult = await resumableUpload({
      endpoint: buildResumableEndpoint(supabaseUrl),
      bucket: BUCKET,
      objectPath: storagePath,
      blob,
      accessToken: session.access_token,
      anonKey,
      contentType: AUDIO_MIME,
      onProgress: input.onProgress,
    });
    if (!uploadResult.ok) return uploadResult;
  }

  // Statut au repos après synchro : `captured` (audio stocké, IA NON lancée).
  // L'analyse est désormais à la demande — l'usager déclenche transcription +
  // extraction depuis l'écran de fin ou la liste, et le flux reprend alors
  // captured → transcribing → processing → ready.
  const row = {
    ...mapCaptureToDbRow(capture),
    audio_path: storagePath,
    status: "captured" as const,
  };

  // Idempotence (résilience synchro 2026-06-04) : `ON CONFLICT (id) DO NOTHING`.
  // Le filet de re-synchro est ré-entrant (mount + online + appStateChange) et peut
  // ré-envoyer la MÊME capture : un `.insert` simple lèverait une collision PK 23505
  // → `{ok:false}` → capture relue en boucle, jamais vidée du buffer. `ignoreDuplicates`
  // insère SANS toucher une ligne existante — donc sans écraser le transcript / les
  // items / la revue humaine déjà produits côté serveur (le piège du vrai upsert).
  // Bonus : un orphelin (audio en Storage, ligne absente) dont la copie locale subsiste
  // est ré-inséré au prochain resync.
  const { error: insertErr, status: insertStatus } = await client
    .from("captures")
    .upsert(row, { onConflict: "id", ignoreDuplicates: true });
  if (insertErr) {
    return {
      ok: false,
      error: dbErrorMessage("captures.upload.insert", insertErr, CAPTURE_NON_ENREGISTREE),
      detail: httpDetail(insertErr.message, insertStatus),
    };
  }

  return { ok: true };
}
